import { VbanDetails } from 'src/app/api/autogenerated';
import { PaymentMethodDetails } from 'src/app/types/PaymentDetails';

const CURRENCY_CLOUD_BANK_NAME = 'The Currency Cloud Limited';
const CURRENCY_CLOUD_BANK_ADDRESS =
  '12 Steward Street, The Steward Building, London, E1 6FQ, GB';

const USD_DEFAULT_BANK_NAME = 'JPMORGAN CHASE BANK, N.A';
const USD_DEFAULT_BANK_ADDRESS = '383 Madison Ave, New York, NY 10179, USA';

const CAD_EFT_BANK_NAME = 'Digital Commerce Bank';
const CAD_EFT_BANK_ADDRESS = '736 Meridian Road N.E, Calgary, Alberta, CA';

type VbanPaymentMethodConfig = {
  idSuffix: string;
  paymentMethodType: string;
  routing: string | undefined;
  accountNumber: string;
  accountNumberLabel: string;
  routingTitle: string;
  bankName: string;
  bankAddress: string;
  accountType?: string;
};

function hasValue(value: string | undefined | null): value is string {
  return value != null && value.trim() !== '';
}

function buildPaymentMethodDetail(
  currencyKey: string,
  config: VbanPaymentMethodConfig,
): PaymentMethodDetails {
  return {
    id: `${currencyKey}_${config.idSuffix}`,
    paymentMethodType: config.paymentMethodType,
    accountName: '',
    accountNumber: config.accountNumber,
    accountNumberLabel: config.accountNumberLabel,
    routingTitle: config.routingTitle,
    achRouting: config.routing ?? '',
    accountType: config.accountType ?? '',
    bankName: config.bankName,
    bankAddress: config.bankAddress,
    ifscCode: '',
  };
}

function getRoutingForVbanType(vban: VbanDetails): string {
  switch (vban.type) {
    case 'ACH':
      return vban.domestic_credit ?? '';
    case 'RTP':
      return vban.domestic_fast_credit ?? '';
    case 'FEDWIRE':
      return vban.domestic_wire ?? '';
    case 'SWIFT':
      return vban.global_wire ?? '';
    default:
      return (
        vban.domestic_credit ??
        vban.domestic_fast_credit ??
        vban.domestic_wire ??
        vban.global_wire ??
        ''
      );
  }
}

function mapUsdTypedVbanEntry(vban: VbanDetails): PaymentMethodDetails | null {
  if (!vban.type) {
    return null;
  }

  const accountNumber = vban.number ?? '';
  const bankName = vban.bank_name ?? USD_DEFAULT_BANK_NAME;
  const bankAddress = USD_DEFAULT_BANK_ADDRESS;
  const accountType = vban.account_type ?? 'Business Checking';
  const routing = getRoutingForVbanType(vban);

  if (!hasValue(accountNumber) && !hasValue(routing)) {
    return null;
  }

  switch (vban.type) {
    case 'ACH':
      return buildPaymentMethodDetail('usd', {
        idSuffix: 'ach',
        paymentMethodType: 'ACH',
        routing,
        accountNumber,
        accountNumberLabel: 'Account Number',
        routingTitle: 'Routing Number',
        bankName,
        bankAddress,
        accountType,
      });
    case 'FEDWIRE':
      return buildPaymentMethodDetail('usd', {
        idSuffix: 'fedwire',
        paymentMethodType: 'FEDWIRE',
        routing,
        accountNumber,
        accountNumberLabel: 'Account Number',
        routingTitle: 'ABA Code / Routing Number',
        bankName,
        bankAddress,
        accountType,
      });
    case 'SWIFT':
      return buildPaymentMethodDetail('usd', {
        idSuffix: 'swift',
        paymentMethodType: 'SWIFT',
        routing,
        accountNumber,
        accountNumberLabel: 'Account Number',
        routingTitle: 'BIC Code',
        bankName,
        bankAddress,
        accountType,
      });
    default:
      return null;
  }
}

function mapUsdVbanDetails(vban: VbanDetails): PaymentMethodDetails[] {
  const currencyKey = 'usd';
  const accountNumber = vban.number ?? '';
  const bankName = vban.bank_name ?? USD_DEFAULT_BANK_NAME;
  const bankAddress = USD_DEFAULT_BANK_ADDRESS;
  const accountType = vban.account_type ?? 'Business Checking';
  const methods: VbanPaymentMethodConfig[] = [];

  if (hasValue(vban.domestic_credit)) {
    methods.push({
      idSuffix: 'ach',
      paymentMethodType: 'ACH',
      routing: vban.domestic_credit,
      accountNumber,
      accountNumberLabel: 'Account Number',
      routingTitle: 'Routing Number',
      bankName,
      bankAddress,
      accountType,
    });
  }
  if (hasValue(vban.domestic_wire)) {
    methods.push({
      idSuffix: 'fedwire',
      paymentMethodType: 'FEDWIRE',
      routing: vban.domestic_wire,
      accountNumber,
      accountNumberLabel: 'Account Number',
      routingTitle: 'ABA Code / Routing Number',
      bankName,
      bankAddress,
      accountType,
    });
  }
  if (hasValue(vban.global_wire)) {
    methods.push({
      idSuffix: 'swift',
      paymentMethodType: 'SWIFT',
      routing: vban.global_wire,
      accountNumber,
      accountNumberLabel: 'Account Number',
      routingTitle: 'BIC Code',
      bankName,
      bankAddress,
      accountType,
    });
  }

  if (methods.length === 0) {
    const typedEntry = mapUsdTypedVbanEntry(vban);
    if (typedEntry) {
      return [typedEntry];
    }
  }

  return methods.map((config) => buildPaymentMethodDetail(currencyKey, config));
}

function mapCadVbanDetails(vban: VbanDetails): PaymentMethodDetails[] {
  const currencyKey = 'cad';
  const methods: VbanPaymentMethodConfig[] = [];

  if (hasValue(vban.domestic_credit)) {
    methods.push({
      idSuffix: 'eft',
      paymentMethodType: 'EFT',
      routing: vban.domestic_credit,
      accountNumber: vban.number ?? '',
      accountNumberLabel: 'Account Number',
      routingTitle: 'Routing Number',
      bankName: CAD_EFT_BANK_NAME,
      bankAddress: CAD_EFT_BANK_ADDRESS,
      accountType: vban.account_type,
    });
  }
  if (hasValue(vban.global_wire)) {
    methods.push({
      idSuffix: 'swift',
      paymentMethodType: 'SWIFT',
      routing: vban.global_wire,
      accountNumber: vban.number ?? '',
      accountNumberLabel: 'Account Number',
      routingTitle: 'BIC Code',
      bankName: CURRENCY_CLOUD_BANK_NAME,
      bankAddress: CURRENCY_CLOUD_BANK_ADDRESS,
      accountType: vban.account_type,
    });
  }

  return methods.map((config) => buildPaymentMethodDetail(currencyKey, config));
}

function mapEurVbanDetails(vban: VbanDetails): PaymentMethodDetails[] {
  const currencyKey = 'eur';
  const iban = vban.iban ?? vban.number ?? '';
  const methods: VbanPaymentMethodConfig[] = [];
  const sepaCreditRouting = vban.domestic_credit ?? vban.domestic_debit ?? '';

  // EUR VBAN accounts expose SEPA Credit whenever an IBAN is present (BIC optional).
  if (hasValue(iban)) {
    methods.push({
      idSuffix: 'sepa_credit',
      paymentMethodType: 'SEPA Credit',
      routing: sepaCreditRouting,
      accountNumber: iban,
      accountNumberLabel: 'IBAN',
      routingTitle: 'BIC (Optional)',
      bankName: CURRENCY_CLOUD_BANK_NAME,
      bankAddress: CURRENCY_CLOUD_BANK_ADDRESS,
      accountType: vban.account_type,
    });
  }
  if (hasValue(vban.domestic_fast_credit)) {
    methods.push({
      idSuffix: 'sepa_instant',
      paymentMethodType: 'SEPA Instant',
      routing: vban.domestic_fast_credit,
      accountNumber: iban,
      accountNumberLabel: 'IBAN',
      routingTitle: 'BIC (Optional)',
      bankName: CURRENCY_CLOUD_BANK_NAME,
      bankAddress: CURRENCY_CLOUD_BANK_ADDRESS,
      accountType: vban.account_type,
    });
  }
  if (hasValue(vban.global_wire)) {
    methods.push({
      idSuffix: 'swift',
      paymentMethodType: 'SWIFT',
      routing: vban.global_wire,
      accountNumber: iban,
      accountNumberLabel: 'IBAN',
      routingTitle: 'BIC Code',
      bankName: CURRENCY_CLOUD_BANK_NAME,
      bankAddress: CURRENCY_CLOUD_BANK_ADDRESS,
      accountType: vban.account_type,
    });
  }

  return methods.map((config) => buildPaymentMethodDetail(currencyKey, config));
}

function mapGbpVbanDetails(vban: VbanDetails): PaymentMethodDetails[] {
  const currencyKey = 'gbp';
  const methods: VbanPaymentMethodConfig[] = [];

  if (hasValue(vban.domestic_fast_credit)) {
    methods.push({
      idSuffix: 'fps',
      paymentMethodType: 'FPS',
      routing: vban.domestic_fast_credit,
      accountNumber: vban.number ?? '',
      accountNumberLabel: 'Account Number',
      routingTitle: 'Sort Code',
      bankName: CURRENCY_CLOUD_BANK_NAME,
      bankAddress: CURRENCY_CLOUD_BANK_ADDRESS,
      accountType: vban.account_type,
    });
  }
  if (hasValue(vban.global_wire)) {
    methods.push({
      idSuffix: 'swift',
      paymentMethodType: 'SWIFT',
      routing: vban.global_wire,
      accountNumber: vban.iban ?? vban.number ?? '',
      accountNumberLabel: 'IBAN',
      routingTitle: 'BIC Code',
      bankName: CURRENCY_CLOUD_BANK_NAME,
      bankAddress: CURRENCY_CLOUD_BANK_ADDRESS,
      accountType: vban.account_type,
    });
  }

  return methods.map((config) => buildPaymentMethodDetail(currencyKey, config));
}

function mapSwiftAccountNumberVbanDetails(
  vban: VbanDetails,
  currencyKey: string,
): PaymentMethodDetails[] {
  if (!hasValue(vban.global_wire)) {
    return [];
  }

  return [
    buildPaymentMethodDetail(currencyKey, {
      idSuffix: 'swift',
      paymentMethodType: 'SWIFT',
      routing: vban.global_wire,
      accountNumber: vban.number ?? '',
      accountNumberLabel: 'Account Number',
      routingTitle: 'BIC Code',
      bankName: CURRENCY_CLOUD_BANK_NAME,
      bankAddress: CURRENCY_CLOUD_BANK_ADDRESS,
      accountType: vban.account_type,
    }),
  ];
}

function mapVbanDetailsToPaymentMethods(
  vban: VbanDetails,
): PaymentMethodDetails[] {
  const currencyKey = (vban.currency ?? 'USD').toLowerCase();

  switch (currencyKey) {
    case 'usd':
      return mapUsdVbanDetails(vban);
    case 'cad':
      return mapCadVbanDetails(vban);
    case 'eur':
      return mapEurVbanDetails(vban);
    case 'gbp':
      return mapGbpVbanDetails(vban);
    case 'sgd':
    case 'aud':
    case 'aed':
      return mapSwiftAccountNumberVbanDetails(vban, currencyKey);
    default:
      return [];
  }
}

export function mapVbanDetailsListToPaymentMethodsByCurrency(
  vbanDetailsList: VbanDetails[] | undefined,
): Record<string, PaymentMethodDetails[]> {
  const result: Record<string, PaymentMethodDetails[]> = {};

  if (!vbanDetailsList?.length) {
    return result;
  }

  vbanDetailsList.forEach((vban) => {
    if (!vban.currency) {
      return;
    }
    const currencyKey = vban.currency.toLowerCase();
    const mappedMethods = mapVbanDetailsToPaymentMethods(vban);

    if (!result[currencyKey]) {
      result[currencyKey] = [];
    }

    mappedMethods.forEach((method) => {
      const alreadyAdded = result[currencyKey].some(
        (existing) =>
          existing.id === method.id ||
          existing.paymentMethodType === method.paymentMethodType,
      );
      if (!alreadyAdded) {
        result[currencyKey].push(method);
      }
    });
  });

  return result;
}
